import { ApiError, NetworkError, type ErrorCode } from '@/api/client'
import { translate } from '../plugins/i18n'

/**
 * What a failed call says to the person who made it, in their language.
 *
 * An `ApiError`'s `message` is English written for a developer — « role 'editor' in this workspace
 * does not hold … » — and may change at any time (`ErrorBody.message`), so it is never shown. What
 * is shown depends on the failure's kind:
 *
 * - a refusal is said by its code (`ErrorBody.code`), in the person's language: a permission
 *   missing, a call asked too often, a row changed meanwhile, a value refused, a row gone, a body
 *   too large, a service down. A screen that knows more about one of them — a name already taken
 *   is a `conflict` too — says its own sentence before it asks here;
 * - a request that never got an answer (`NetworkError`) says the network failed, and nothing
 *   else does — a script's own bug is a `TypeError` too, and is no connection to check;
 * - anything else is the screen's own `fallback`, which names what did not happen.
 */
export function failureMessage(error: unknown, fallback: string): string {
  return refusalMessage(error) ?? fallback
}

/**
 * The sentence for the failures every screen words the same way, or `null` when the screen's own
 * wording applies — a server error, or anything that is not a refused call. For a call site that keeps a sentence of its own for some refusals — a field the
 * server refused, a name already taken — and must still say the common ones.
 */
export function refusalMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    if (isForbidden(error)) return translate('core.errors.forbidden')
    const key = error.code ? BY_CODE[error.code] : BY_STATUS[error.status]
    return key ? translate(key) : null
  }
  if (error instanceof NetworkError) return translate('core.errors.networkError')
  return null
}

/** The codes every screen words the same way. `internal` and the 5xx are the screen's own failure. */
const BY_CODE: Partial<Record<ErrorCode, string>> = {
  rate_limited: 'core.errors.rateLimited',
  conflict: 'core.errors.conflict',
  validation: 'core.errors.validation',
  not_found: 'core.errors.notFound',
  payload_too_large: 'core.errors.tooLarge',
  service_unavailable: 'core.errors.unavailable',
  timed_out: 'core.errors.unavailable',
  upstream_error: 'core.errors.unavailable',
}

/** The same, for a body that carried no code — a proxy's page, a status line alone. */
const BY_STATUS: Partial<Record<number, string>> = {
  404: 'core.errors.notFound',
  409: 'core.errors.conflict',
  413: 'core.errors.tooLarge',
  422: 'core.errors.validation',
  429: 'core.errors.rateLimited',
  502: 'core.errors.unavailable',
  503: 'core.errors.unavailable',
  504: 'core.errors.unavailable',
}

/**
 * Whether the server refused for want of a permission. The two other `403`s are states with a
 * remedy of their own, which the client's hooks already take in hand: no organization yet, and a
 * second factor to prove.
 */
export function isForbidden(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === 'forbidden' || (error.status === 403 && error.code === undefined))
  )
}
